/* Schweiger collection navigation. Numbered links remain the no-JS fallback. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('sd-collection-load-more')) return;
  let restorationChecked = false;

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      // A parser-created element can connect before its children are available.
      queueMicrotask(() => this.init());
    }

    init() {
      if (!this.isConnected || this.events || this.dataset.page !== '1') return;
      this.scope = this.closest('[data-sd-collection-products]');
      const firstCard = this.scope?.querySelector('.grid-product[data-product-id]');
      if (!firstCard || !window.fetch) return;
      this.grid = firstCard.parentElement;
      this.context = location.pathname + location.search;
      this.cacheKey = 'schweiger:collection:' + this.dataset.sectionId + ':' + this.context;
      this.pages = [];
      this.visible = Number(this.dataset.visible);
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      window.addEventListener('pagehide', () => this.save(), options);
      this.setAttribute('data-enhanced', '');
      if (!restorationChecked) {
        restorationChecked = true;
        this.restore();
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.events = null;
    }

    nextLink() { return this.querySelector('[data-load-more-next]'); }
    status(message) { this.querySelector('[data-load-more-status]').textContent = message; }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      const link = this.nextLink();
      if (link) {
        link.textContent = busy ? 'Loading products…' : 'Load more products';
        if (busy) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
      }
    }

    validURL(value) {
      const url = new URL(value, location.href);
      return url.origin === location.origin && url.pathname === location.pathname ? url : null;
    }

    async onClick(event) {
      const link = event.target.closest('[data-load-more-next]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = this.validURL(link.href);
      if (!url) return;
      event.preventDefault();
      if (this.busy) return;
      this.setBusy(true);
      this.status('Loading more products.');
      this.request = new AbortController();
      const timer = setTimeout(() => this.request.abort(), 20000);
      try {
        const requestURL = new URL(url);
        requestURL.hash = '';
        requestURL.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(requestURL, { signal: this.request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed.');
        const html = await response.text();
        if (!this.isConnected || this.context !== location.pathname + location.search) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(parsed.querySelectorAll('sd-collection-load-more'))
          .find(element => element.dataset.sectionId === this.dataset.sectionId);
        if (!incoming || Number(incoming.dataset.page) !== Number(url.searchParams.get('page') || 1)) {
          throw new Error('Unexpected collection response.');
        }
        const incomingScope = incoming.closest('[data-sd-collection-products]');
        const first = incomingScope?.querySelector('.grid-product[data-product-id]');
        if (!first) throw new Error('No product grid returned.');
        const cards = Array.from(first.parentElement.children).filter(card => card.matches('.grid-product[data-product-id]'));
        const next = incoming.querySelector('[data-load-more-next]')?.getAttribute('href') || '';
        if (next && !this.validURL(next)) throw new Error('Unexpected next page.');
        const page = { cards: cards.map(card => card.outerHTML), next };
        const position = { left: scrollX, top: scrollY };
        const appended = this.append(page);
        if (!appended.length) throw new Error('No new products returned.');
        this.pages.push(page);
        this.initializeCards(appended);
        // Keep the reader at the join between the old and new products.
        this.focusFirst(appended);
        window.scrollTo({ ...position, behavior: 'instant' });
        this.status(`${appended.length} more products loaded. ${this.visible} of ${this.dataset.total} products shown.${next ? '' : ' All products loaded.'}`);
        this.removeAttribute('data-show-fallback');
      } catch (error) {
        if (this.isConnected && this.context === location.pathname + location.search) {
          this.status('Products could not load. Try again, or use the page numbers below.');
          this.setAttribute('data-show-fallback', '');
        }
      } finally {
        clearTimeout(timer);
        if (this.isConnected) this.setBusy(false);
      }
    }

    append(page) {
      const ids = new Set(Array.from(this.grid.children, card => card.dataset.productId));
      const appended = [];
      for (const html of page.cards) {
        const template = document.createElement('template');
        template.innerHTML = html;
        const card = template.content.firstElementChild;
        if (!card?.matches('.grid-product[data-product-id]') || ids.has(card.dataset.productId)) continue;
        ids.add(card.dataset.productId);
        this.grid.append(card);
        appended.push(card);
      }
      this.visible += appended.length;
      this.querySelector('[data-load-more-count]').textContent = `${this.visible} of ${this.dataset.total} products`;
      const link = this.nextLink();
      if (page.next && link) link.href = page.next;
      else link?.remove();
      return appended;
    }

    initializeCards(cards) {
      // Use the same modal preload behavior as this theme, scoped to new cards.
      const theme = window.theme;
      for (const card of cards) {
        if (theme?.settings?.quickView && theme.preloadProductModal) {
          const preload = () => {
            if (theme.config.bpSmall) return;
            card.removeEventListener('mouseover', preload);
            card.removeEventListener('focusin', preload);
            theme.preloadProductModal(card.dataset.productHandle, card.dataset.productId, card.querySelector('.quick-product__btn'));
          };
          card.addEventListener('mouseover', preload);
          card.addEventListener('focusin', preload);
        }
        theme?.Collection?.prototype.colorSwatchHovering.call({ container: card });
      }
      window.AOS?.refreshHard();
      // Existing collection integrations can initialize labels and other card UI.
      document.dispatchEvent(new CustomEvent('collection:reloaded', { detail: { appended: true, items: cards, sectionId: this.dataset.sectionId } }));
    }

    focusFirst(cards) {
      cards[0]?.querySelector('.grid-product__link')?.focus({ preventScroll: true });
    }

    save() {
      if (!this.pages.length || this.context !== location.pathname + location.search) return;
      try {
        const value = JSON.stringify({ time: Date.now(), pages: this.pages, top: scrollY, total: this.dataset.total });
        // Cache only this tab's recently viewed collection, within a modest limit.
        if (value.length < 1500000) sessionStorage.setItem(this.cacheKey, value);
      } catch (_) { /* Browsing still works when storage is unavailable. */ }
    }

    restore() {
      // BFCache already retains DOM. This covers a real Back/Forward reload.
      if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
        if (!saved || Date.now() - saved.time > 600000 || saved.total !== this.dataset.total || !Array.isArray(saved.pages)) return;
        const cards = [];
        for (const page of saved.pages) {
          if (!Array.isArray(page.cards) || (page.next && !this.validURL(page.next))) break;
          cards.push(...this.append(page));
          this.pages.push(page);
        }
        this.initializeCards(cards);
        requestAnimationFrame(() => {
          if (this.isConnected && Number.isFinite(saved.top)) window.scrollTo({ top: saved.top, behavior: 'instant' });
        });
      } catch (_) { /* Keep the normal first page if the cache is unavailable. */ }
    }
  }
  customElements.define('sd-collection-load-more', CollectionLoadMore);
})();
